// 라우트 컴포넌트 (인증 상태에 따라 조건부 렌더링)

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedLayout from './ProtectedLayout';
import ScrollLayout from '../components/Layout/ScrollLayout';
import { ROUTES } from '../constants/routes';
import Admins from '../pages/Admins/Admins';
import ChangePassword from '../pages/ChangePassword/ChangePassword';
import CourseDetail from '../pages/CourseDetail/CourseDetail';
import CourseEdit from '../pages/CourseEdit/CourseEdit';
import Courses from '../pages/Courses/Courses';
import Dashboard from '../pages/Dashboard/Dashboard';
import EnrollmentDetail from '../pages/enrollment/EnrollmentDetail/EnrollmentDetail';
import Enrollments from '../pages/enrollment/Enrollments/Enrollments';
import InstructorDetail from '../pages/InstructorDetail/InstructorDetail';
import InstructorEdit from '../pages/InstructorEdit/InstructorEdit';
import Instructors from '../pages/Instructors/Instructors';
import LessonDetail from '../pages/LessonDetail/LessonDetail';
import LessonEdit from '../pages/LessonEdit/LessonEdit';
import Login from '../pages/Login/Login';
import OrderSheetDetail from '../pages/OrderSheets/OrderSheetDetail';
import PaymentDetail from '../pages/Payments/PaymentDetail';
import Payments from '../pages/Payments/Payments';
import ProductDetail from '../pages/ProductDetail/ProductDetail';
import ProductEdit from '../pages/ProductEdit/ProductEdit';
import Products from '../pages/Products/Products';
import Profile from '../pages/Profile/Profile';
import Settings from '../pages/Settings/Settings';
import UserDetail from '../pages/UserDetail/UserDetail';
import Users from '../pages/Users/Users';
import Deliveries from '@/pages/delivery/Deliveries/Deliveries';
import DeliveryDetail from '@/pages/delivery/DeliveryDetail/DeliveryDetail';
import InstitutionCreate from '@/pages/institution/InstitutionCreate/InstitutionCreate';
import InstitutionDetail from '@/pages/institution/InstitutionDetail/InstitutionDetail';
import InstitutionEdit from '@/pages/institution/InstitutionEdit/InstitutionEdit';
import Institution from '@/pages/institution/Institutions/Institutions';

interface AppRoutesProps {
  isAuthenticated: boolean;
}

const AppRoutes: React.FC<AppRoutesProps> = ({ isAuthenticated }) => {
  const location = useLocation();

  return (
    <Routes>
      {/* 로그인 페이지: 인증되지 않은 사용자만 접근 가능 */}
      <Route
        path={ROUTES.LOGIN}
        element={isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace state={{ from: location }} /> : <Login />}
      />

      {/* 보호된 라우트: 인증된 사용자만 접근 가능 */}
      <Route path="/" element={<ProtectedLayout />}>
        {/* Scrollable Pages (Dashboard, Details, Forms, etc.) */}
        <Route element={<ScrollLayout />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="dashboard/users/:userId" element={<UserDetail />} />
          <Route path="dashboard/user/:userId/enrollment/:enrollmentId" element={<EnrollmentDetail />} />
          <Route path="dashboard/delivery/:id" element={<DeliveryDetail />} />
          <Route path="dashboard/institutions" element={<Institution />} />
          <Route path="dashboard/institutions/new" element={<InstitutionCreate />} />
          <Route path="dashboard/institutions/:id" element={<InstitutionDetail />} />
          <Route path="dashboard/institutions/:id/edit" element={<InstitutionEdit />} />
          <Route path="dashboard/courses/new" element={<CourseEdit />} />
          <Route path="dashboard/courses/:id" element={<CourseDetail />} />
          <Route path="dashboard/courses/:id/edit" element={<CourseEdit />} />
          <Route path="dashboard/instructors/new" element={<InstructorEdit />} />
          <Route path="dashboard/instructors/:id" element={<InstructorDetail />} />
          <Route path="dashboard/instructors/:id/edit" element={<InstructorEdit />} />
          <Route path="dashboard/lessons/:id" element={<LessonDetail />} />
          <Route path="dashboard/lessons/:id/edit" element={<LessonEdit />} />
          <Route path="dashboard/courses/:courseId/chapters/:chapterId/lessons/new" element={<LessonEdit />} />
          <Route path="dashboard/courses/:courseId/chapters/:chapterId/lessons/:id/edit" element={<LessonEdit />} />
          <Route path="dashboard/products" element={<Products />} />
          <Route path="dashboard/products/new" element={<ProductEdit />} />
          <Route path="dashboard/products/:id" element={<ProductDetail />} />
          <Route path="dashboard/products/:productId/edit" element={<ProductEdit />} />
          <Route path="dashboard/payments/:id" element={<PaymentDetail />} />
          <Route path="dashboard/order-sheets/:id" element={<OrderSheetDetail />} />
          <Route path="dashboard/profile" element={<Profile />} />
          <Route path="dashboard/profile/change-password" element={<ChangePassword />} />

          <Route path="dashboard/settings" element={<Settings />} />
        </Route>

        {/* Scroll Pages (List Pages with page scrolling) */}
        <Route element={<ScrollLayout />}>
          <Route path="dashboard/admins" element={<Admins />} />
          <Route path="dashboard/users" element={<Users />} />
          <Route path="dashboard/courses" element={<Courses />} />
          <Route path="dashboard/instructors" element={<Instructors />} />
          <Route path="dashboard/payments" element={<Payments />} />
          <Route path="dashboard/enrollments" element={<Enrollments />} />
          <Route path="dashboard/deliveries" element={<Deliveries />} />
        </Route>

        {/* 루트 경로(/) 접근 시에만 대시보드로 리다이렉트 */}
        {/* index 라우트는 부모 라우트(path="/")와 정확히 일치하는 경로에만 매칭됨 */}
        <Route index element={<Navigate to={ROUTES.DASHBOARD} replace state={{ from: location }} />} />
      </Route>

      {/* 404: 인증 상태에 따라 리다이렉트 */}
      <Route path="*" element={<Navigate to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.LOGIN} replace />} />
    </Routes>
  );
};

export default AppRoutes;
